import { NoticeTypes } from '@genial/components';
import { BasketMarketOfferEntity, BasketOfferEntity } from '@genial/services';
import {
  ResponsesCoeOfferResponse,
  ResponsesEquityResponse,
  ResponsesFixedIncomeOfferResponse,
  ResponsesFundOfferResponse,
  ResponsesTreasuryOfferResponse,
} from '@genial/services/dist/models';
import { FixedIncomeOrderRequestedOfferDetailEntity } from '@genial/services/src';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';

import { SignedInRootStackParamList } from '../../../routes/types';
import { basketCustomerOrderResumeState } from '../../../store/batchTicket/atom';
import { FieldType, fieldsTransform } from '../../../store/batchTicket/normalize';
import {
  normalizeDirectTreasuryOffers,
  normalizeFixedIncomeOffers,
  normalizeFundsOffers,
} from '../../../store/product/normalizer';
import { normalizeCoesOffers } from '../../../store/product/normalizer/coes';
import { normalizeEquityOffers } from '../../../store/product/normalizer/equity';
import { noticeRequiredAttentionDetail, noticeTotalSummaryDetail } from '../utils';
import { AddedProductItemData } from '../utils/types';

type NormalizeByMarketParams = {
  market: FieldType;
  offer: BasketOfferEntity;
  requestedOfferDetail?: FixedIncomeOrderRequestedOfferDetailEntity;
};

const normalizeFixedIncomeOffersWithFee = (
  offer: ResponsesFixedIncomeOfferResponse[],
  requestedOfferDetail?: FixedIncomeOrderRequestedOfferDetailEntity
) => {
  const normalizedOffer = normalizeFixedIncomeOffers(offer)[0];
  return [
    {
      ...normalizedOffer,
      orderFee: normalizedOffer.distributionFeesNormalized.find(
        (fee) => fee.feeId === requestedOfferDetail?.feeId
      ),
    },
  ];
};

const normalizeFunctions = {
  fixedIncome: normalizeFixedIncomeOffersWithFee,
  equity: normalizeEquityOffers,
  funds: normalizeFundsOffers,
  directTreasury: normalizeDirectTreasuryOffers,
  coes: normalizeCoesOffers,
} as const;

type MarketDataType = ResponsesFixedIncomeOfferResponse &
  ResponsesEquityResponse &
  ResponsesFundOfferResponse &
  ResponsesTreasuryOfferResponse &
  ResponsesCoeOfferResponse;

export const useBatchTicketResumeDetail = () => {
  const { canGoBack, goBack } = useNavigation();
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'BatchTicketResumeDetail'>>();
  const { basketCustomerOrderId, type } = params;
  const basketCustomerOrderResume = useRecoilValue(basketCustomerOrderResumeState);

  const basketCustomerOrderItem = basketCustomerOrderResume.data.all.find(
    (item) => item.basketCustomerOrderId === basketCustomerOrderId
  );

  const normalizeByMarket = ({ market, offer, requestedOfferDetail }: NormalizeByMarketParams) => {
    const marketIsEquity = market === 'equity';
    const normalizeFieldName = marketIsEquity ? 'equity' : 'offer';
    const offerData = marketIsEquity ? { quote: offer } : offer;

    const data = [{ [normalizeFieldName]: offerData }] as unknown as MarketDataType[];

    return normalizeFunctions[market](data, requestedOfferDetail)[0];
  };

  const transformObject = (input: BasketMarketOfferEntity) => {
    const { marketType, basketOffers, totalMarketOffers } = input;

    const translatedMarketType: FieldType = fieldsTransform[marketType!] || marketType;

    const items = basketOffers
      ?.map((item) => {
        if (!item) {
          return null;
        }

        const offerNormalized = normalizeByMarket({
          market: translatedMarketType,
          offer: item.offer,
          requestedOfferDetail: item.requestedOfferDetail,
        });

        const isRequiredAttention = type === 'requiredAttention';
        const hasErrors = item.errors && item.errors.length > 0;

        if (isRequiredAttention && hasErrors) {
          return { ...item.offer, ...offerNormalized, ...item, errors: item.errors };
        }

        if (!isRequiredAttention && !hasErrors) {
          return { ...item.offer, ...offerNormalized, ...item };
        }

        return null;
      })
      .filter(Boolean);

    const totalMarketAmount =
      items?.reduce((acc, item) => {
        if (item?.marketType === 'Equity') {
          return acc + item.quantity * item.requestedPrice;
        }

        return acc + Number(item?.amount);
      }, 0) || 0;

    return {
      type: translatedMarketType,
      percentage: 0,
      assetCount: totalMarketOffers || 0,
      total: totalMarketAmount,
      totalFormatted: Utils.Mask.Money(totalMarketAmount),
      items,
    };
  };

  const itemsAddedData = basketCustomerOrderItem?.customerOffers?.map((offer) =>
    transformObject(offer)
  ) as unknown as AddedProductItemData[];

  const addedItemsIsEmpty = itemsAddedData?.length === 0;

  const handleGoBack = useCallback(() => {
    if (canGoBack()) {
      goBack();
    }
  }, [canGoBack, goBack]);

  const getSliderModalWidth = (isDesktop: boolean) => (isDesktop ? 0.8 : 1);

  const routeParamTypeIsTotalSummary = type === 'totalSummary';

  const noticeProps = {
    textObjects: routeParamTypeIsTotalSummary
      ? noticeTotalSummaryDetail
      : noticeRequiredAttentionDetail,
    testId: 'notice',
    ...(!routeParamTypeIsTotalSummary && {
      backgroundColor: Utils.ColorToken.WithOpacity('feedback.attention.lightest', 0.4),
    }),
    type: routeParamTypeIsTotalSummary ? 'alert' : 'warning',
  } as NoticeTypes.Props;

  return {
    basketCustomerOrderItem,
    addedItemsIsEmpty,
    itemsAddedData,
    noticeProps,
    handleGoBack,
    getSliderModalWidth,
  };
};
